// src/utils/dateUtils.ts (or inline in components)

/**
 * Formats a Gregorian date according to a .NET-style format string.
 * Supports: d, dd, MM, yyyy, MMM, MMMM, ddd, dddd
 */
export const formatGregorianDate = (date: Date, format: string): string => {
  if (!format) return date.toLocaleDateString("en-US");

  const day = date.getDate().toString();
  const dayPadded = day.padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  const monthName = date.toLocaleDateString("en-US", { month: "long" });
  const shortMonth = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const shortWeekday = date.toLocaleDateString("en-US", { weekday: "short" });

  const tokenValues: Record<string, string> = {
    dddd: weekday,
    ddd: shortWeekday,
    MMMM: monthName,
    MMM: shortMonth,
    yyyy: year,
    MM: month,
    dd: dayPadded,
    d: day,
  };

  return format.replace(
    /dddd|ddd|MMMM|MMM|yyyy|MM|dd|d/g,
    (token) => tokenValues[token] ?? token,
  );
};

type IslamicMonthKey =
  | "muharram"
  | "safar"
  | "rabeeUlAwwal"
  | "rabeeUlAkhir"
  | "jamadahAlAwwal"
  | "jamadahAlAkhir"
  | "rajab"
  | "shaban"
  | "ramadhan"
  | "shawwal"
  | "dhulQadah"
  | "dhulHijjah";

type IslamicMonthNames = Record<IslamicMonthKey, string> &
  Partial<Record<`${IslamicMonthKey}Short`, string>>;

// Includes the backend's own spellings (HijriDate.MonthName), which is what
// the timetable JSON actually carries
const monthKeyByName: Record<string, IslamicMonthKey> = {
  muharram: "muharram",
  محرم: "muharram",
  safar: "safar",
  صفر: "safar",
  "rabi al-awwal": "rabeeUlAwwal",
  "rabee ul-awwal": "rabeeUlAwwal",
  "rabi ul-awwal": "rabeeUlAwwal",
  "ربيع الأول": "rabeeUlAwwal",
  "rabi al-thani": "rabeeUlAkhir",
  "rabee ul-akhir": "rabeeUlAkhir",
  "rabi ul-akhir": "rabeeUlAkhir",
  "ربيع الآخر": "rabeeUlAkhir",
  "jamadah al-awwal": "jamadahAlAwwal",
  "jumada al-awwal": "jamadahAlAwwal",
  "جمادى الأولى": "jamadahAlAwwal",
  "jumada al-thani": "jamadahAlAkhir",
  "jamadah al-akhir": "jamadahAlAkhir",
  "jumada al-akhir": "jamadahAlAkhir",
  "جمادى الآخرة": "jamadahAlAkhir",
  rajab: "rajab",
  رجب: "rajab",
  "sha'ban": "shaban",
  shaban: "shaban",
  shaaban: "shaban",
  شعبان: "shaban",
  ramadhan: "ramadhan",
  ramadan: "ramadhan",
  رمضان: "ramadhan",
  shawwal: "shawwal",
  شوال: "shawwal",
  "dhu al-qadah": "dhulQadah",
  "dhul qadah": "dhulQadah",
  "zul qadah": "dhulQadah",
  "ذو القعدة": "dhulQadah",
  "dhu al-hijjah": "dhulHijjah",
  "dhul hijjah": "dhulHijjah",
  "zul hijjah": "dhulHijjah",
  "ذو الحجة": "dhulHijjah",
};

const findMonthKey = (monthName: string): IslamicMonthKey | undefined => {
  const normalized = monthName.toLowerCase().trim();
  if (monthKeyByName[normalized]) return monthKeyByName[normalized];
  for (const [name, key] of Object.entries(monthKeyByName)) {
    if (normalized.includes(name) || name.includes(normalized)) return key;
  }
  return undefined;
};

/**
 * Formats a Hijri date according to a .NET-style format string.
 * Assumes Hijri components: day (string), monthName (string), year (string).
 * Supports: d, dd, yyyy, MMM (short month name), MMMM (full month name)
 * Uses customized month names from screenData when available
 */
export const formatHijriDate = (
  day: string,
  monthName: string,
  year: string,
  format: string,
  customizedNames?: IslamicMonthNames,
): string => {
  if (!format) return `${day} ${monthName} ${year}`;

  const parsedDay = parseInt(day, 10);
  const dayWithoutLeadingZero = Number.isNaN(parsedDay)
    ? day
    : parsedDay.toString();
  const dayNum = dayWithoutLeadingZero.padStart(2, "0");

  const monthKey = customizedNames ? findMonthKey(monthName) : undefined;
  const fullMonthName =
    (monthKey && customizedNames?.[monthKey]) || monthName;
  // Config cached before short names existed has none; cut to 3 as before
  const shortMonthName =
    (monthKey && customizedNames?.[`${monthKey}Short`]) ||
    fullMonthName.substring(0, 3);

  const tokenValues: Record<string, string> = {
    yyyy: year,
    MMMM: fullMonthName,
    MMM: shortMonthName,
    dd: dayNum,
    d: dayWithoutLeadingZero,
  };

  const result = format.replace(
    /yyyy|MMMM|MMM|dd|d/g,
    (token) => tokenValues[token] ?? token,
  );

  return result;
};
